import { useMap } from "@react-hookz/web";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { notEmpty } from "shared/lib/utils";

import { page as PlusSearchPage } from "scmp-app/pages/plus/search";
import {
  page as SearchPage,
  type RouteFunction as SearchRouteFunction,
} from "scmp-app/pages/search";
import type {
  ArticlesSearchOrderByInput,
  ContentTypeEnum,
} from "scmp-app/queries/__generated__/searchArticleResultPaginationQuery.graphql";

import { DefaultContentTypes } from "./consts";

export type ArticleResultIdMapEntry = {
  page: number;
  position: number;
  resultId?: null | string;
};

export type ArticlesSearchFilterByDateRange = {
  endDate?: number;
  startDate?: number;
};

export type RefineSearchState = {
  articleTypeIds: string[];
  articleTypeText: string;
  contentTypes: ContentTypeEnum[];
  dateRange?: ArticlesSearchFilterByDateRange;
  dateRangeText: string;
  orderBy?: ArticlesSearchOrderByInput;
  paywallTypeIds: string[];
  sectionIds: string[];
};

export const EmptyRefineSearchState: RefineSearchState = {
  articleTypeIds: [],
  articleTypeText: "All",
  contentTypes: [...DefaultContentTypes],
  dateRange: undefined,
  dateRangeText: "All",
  orderBy: undefined,
  paywallTypeIds: [],
  sectionIds: [],
};

export type SearchPageType = "default" | "plus";

type ContextValue = {
  articleResultIdMap?: Map<string, ArticleResultIdMapEntry>;
  currentValue: string;
  embedMode?: boolean;
  embedWidgetOnly?: boolean;
  initialSearchId?: string;
  refineSearch: RefineSearchState;
  searchPageType: SearchPageType;
  searchRouteFunction?: SearchRouteFunction;
  setInitialSearchId?: Dispatch<SetStateAction<string | undefined>>;
  setRefineSearch: Dispatch<SetStateAction<RefineSearchState>>;
};

const SearchContext = createContext<ContextValue>({
  currentValue: "",
  embedMode: false,
  embedWidgetOnly: false,
  refineSearch: EmptyRefineSearchState,
  searchPageType: "default",
  setRefineSearch: () => {},
});

type Props = {
  children: ReactNode;
  currentValue: string;
  embedMode?: boolean;
  embedWidgetOnly?: boolean;
  searchPageType: SearchPageType;
};

export const SearchContextProvider: React.FunctionComponent<Props> = ({
  children,
  currentValue,
  embedMode = false,
  embedWidgetOnly = false,
  searchPageType,
}) => {
  const [refineSearch, setRefineSearch] = useState<RefineSearchState>(EmptyRefineSearchState);
  const [initialSearchId, setInitialSearchId] = useState<string>();
  const articleResultIdMap = useMap<string, ArticleResultIdMapEntry>([]);

  useEffect(() => {
    if (!notEmpty(currentValue)) return;
    setInitialSearchId(undefined);
  }, [currentValue]);

  const searchRouteFunction = useMemo(() => {
    switch (searchPageType) {
      case "default":
        return SearchPage.route;
      case "plus":
        return PlusSearchPage.route;
    }
  }, [searchPageType]);

  useEffect(() => {
    if (currentValue) return;
    setRefineSearch(EmptyRefineSearchState);
    setInitialSearchId(undefined);
    articleResultIdMap.clear();
  }, [articleResultIdMap, currentValue, searchPageType]);

  return (
    <SearchContext.Provider
      value={{
        articleResultIdMap,
        currentValue,
        embedMode,
        embedWidgetOnly,
        initialSearchId,
        refineSearch,
        searchPageType,
        searchRouteFunction,
        setInitialSearchId,
        setRefineSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);
